import React, { useMemo, useState, useEffect, useRef } from 'react';
import { procesarFormulario } from '../../../../admin/form-master/utils/gruposSubgruposProcessor';
import { MIEL_FORM_CONFIG, MIEL_GROUP_PRESELECTION } from '../../config/configMiel.js';
import '../QuestionSelectorTree.css';

// Las etiquetas de Kobo en Miel traen markdown (**Apiario**, ## Título)
const limpiarEtiqueta = (texto) => String(texto || '').replace(/\*\*|__|^#+\s*/gm, '').trim();

const getChildren = (node) => [...(node.gruposHijos || []), ...(node.subgrupos || []), ...(node.preguntas || [])];

// El id de cada pregunta es su name: es la clave del JSON de Mongo
const getAllQuestionIds = (node) => {
  if (node.tipo === 'pregunta') return [node.id];
  return getChildren(node).flatMap(getAllQuestionIds);
};

const MielQuestionSelectorTree = ({ questions, selectedQuestionIds, onSelectionChange }) => {
  const treeData = useMemo(() => {
    if (!questions || questions.length === 0) return [];

    const { grupos = [] } = procesarFormulario(questions);

    const filterNodes = (nodes) =>
      nodes.reduce((acc, node) => {
        if (node.tipo === 'grupo' || node.tipo === 'subgrupo') {
          if (MIEL_FORM_CONFIG.excludeGroupsByName.includes(node.name)) return acc;

          const newNode = {
            ...node,
            gruposHijos: filterNodes(node.gruposHijos || []),
            subgrupos: filterNodes(node.subgrupos || []),
            preguntas: filterNodes(node.preguntas || []),
          };

          // Ocultar grupos que se quedaron sin preguntas
          if (getAllQuestionIds(newNode).length === 0) return acc;

          acc.push(newNode);
          return acc;
        }

        if (!node.name) return acc;
        if (MIEL_FORM_CONFIG.excludeByType.includes(node.type)) return acc;
        if (MIEL_FORM_CONFIG.excludeByName.includes(node.name)) return acc;

        acc.push({ ...node, tipo: 'pregunta', id: node.name });
        return acc;
      }, []);

    return filterNodes(grupos);
  }, [questions]);

  const didApplyPreselectionRef = useRef(false);

  useEffect(() => {
    if (didApplyPreselectionRef.current || treeData.length === 0) return;
    didApplyPreselectionRef.current = true;

    if (!MIEL_GROUP_PRESELECTION.enabled || MIEL_GROUP_PRESELECTION.groups.length === 0) return;

    const merged = new Set(selectedQuestionIds);
    const visit = (node) => {
      if (node.tipo !== 'pregunta' && MIEL_GROUP_PRESELECTION.groups.includes(node.name)) {
        getAllQuestionIds(node).forEach((id) => merged.add(id));
      }
      getChildren(node).forEach(visit);
    };
    treeData.forEach(visit);

    if (merged.size !== selectedQuestionIds.size) onSelectionChange(merged);
  }, [treeData, selectedQuestionIds, onSelectionChange]);

  const isNodeSelected = (node) => {
    const ids = getAllQuestionIds(node);
    return ids.length > 0 && ids.every((id) => selectedQuestionIds.has(id));
  };

  const isNodeIndeterminate = (node) => {
    const ids = getAllQuestionIds(node);
    const count = ids.filter((id) => selectedQuestionIds.has(id)).length;
    return count > 0 && count < ids.length;
  };

  const handleNodeToggle = (node) => {
    const ids = getAllQuestionIds(node);
    const newSelected = new Set(selectedQuestionIds);
    if (isNodeSelected(node)) {
      ids.forEach((id) => newSelected.delete(id));
    } else {
      ids.forEach((id) => newSelected.add(id));
    }
    onSelectionChange(newSelected);
  };

  const handleSingleToggle = (id) => {
    const newSelected = new Set(selectedQuestionIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange(newSelected);
  };

  const renderNode = (node) => {
    if (node.tipo === 'pregunta') {
      const label = limpiarEtiqueta(node.label) || node.name;
      return (
        <div key={node.id} className="tree-node question-node">
          <input
            type="checkbox"
            checked={selectedQuestionIds.has(node.id)}
            onChange={() => handleSingleToggle(node.id)}
          />
          <span className="node-label" onClick={() => handleSingleToggle(node.id)}>
            {label}
            {MIEL_FORM_CONFIG.showName && label !== node.name && (
              <small className="text-muted ml-2">({node.name})</small>
            )}
          </span>
        </div>
      );
    }

    return (
      <MielGroupNode
        key={node.id || node.name}
        node={node}
        isSelected={isNodeSelected(node)}
        isIndeterminate={isNodeIndeterminate(node)}
        totalPreguntas={getAllQuestionIds(node).length}
        onToggle={() => handleNodeToggle(node)}
        renderChildren={renderNode}
      />
    );
  };

  if (!treeData.length) {
    return <div className="text-muted p-3">No hay preguntas disponibles o cargando...</div>;
  }

  return <div className="question-selector-tree">{treeData.map(renderNode)}</div>;
};

const MielGroupNode = ({ node, isSelected, isIndeterminate, totalPreguntas, onToggle, renderChildren }) => {
  const [expanded, setExpanded] = useState(false);
  const checkboxRef = useRef();

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  const sortedChildren = getChildren(node).sort((a, b) => (a.indiceOriginal || 0) - (b.indiceOriginal || 0));
  const groupLabel = limpiarEtiqueta(node.label) || node.name;

  return (
    <div className={`tree-node group-node type-${node.preguntaOriginal?.type || node.tipo || 'group'}`}>
      <div className="group-header">
        <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? '▼' : '▶'}
        </button>
        <input type="checkbox" checked={isSelected} ref={checkboxRef} onChange={onToggle} />
        <span className="node-label group-label" onClick={() => setExpanded(!expanded)}>
          {groupLabel} <small className="text-muted">({totalPreguntas})</small>
        </span>
      </div>
      {expanded && <div className="group-children">{sortedChildren.map(renderChildren)}</div>}
    </div>
  );
};

export default MielQuestionSelectorTree;
